import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield,
  FileText,
  Users,
  Lock,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';

const PrivacyTerms = () => {
  const privacySections = [
    {
      title: "Data Protection",
      icon: Shield,
      content: [
        "We collect only necessary personal and health information required for providing healthcare services",
        "All data is encrypted and stored securely using industry-standard security measures",
        "Personal information is never shared with third parties without explicit consent",
        "We comply with international data protection standards and local regulations"
      ]
    },
    {
      title: "Children's Data",
      icon: Users,
      content: [
        "Child safeguarding and consent protocols for telehealth consultations",
        "Data privacy for minors; encrypted records; limited access by role",
        "Children's data is anonymized in reports and analytics",
        "Parents have the right to access, modify, or delete their child's data",
        "Clinical quality: guidelines, peer review, and case audits for both telehealth and in-person care"
      ]
    },
    {
      title: "Cookies & Tracking",
      icon: Lock,
      content: [
        "We use essential cookies for website functionality and security",
        "Analytics cookies help us improve our website and services",
        "You can control cookie preferences through your browser settings",
        "No personal information is collected through cookies without consent"
      ]
    }
  ];

  const termsSections = [
    {
      title: "Insurance Terms",
      icon: FileText,
      content: [
        "Coverage begins upon enrollment and payment confirmation",
        `Annual premium: $28 USD (15,820 CFA / 2,030 GMD) per child - one price for all families`,
        "Coverage includes 100% coverage at AMHAS Urgent Care Facilities and 100% AI + Telehealth services at school",
        "Exclusions include chronic/terminal illnesses, major surgeries, and extended hospital stays"
      ]
    },
    {
      title: "Exclusions",
      icon: AlertTriangle,
      content: [
        "Chronic or terminal illnesses requiring long-term care",
        "Major surgeries requiring extended hospitalization",
        "Hospital stays longer than 3 months",
        "Pandemic-related illnesses (covered by government programs)",
        "Cosmetic procedures and experimental treatments"
      ]
    },
    {
      title: "Claims & Payments",
      icon: CheckCircle,
      content: [
        "Claims must be submitted within 30 days of service",
        "Payments are processed within 24-48 hours of claim approval",
        "Direct deposit to provider accounts within 3-5 business days",
        "100% payment guarantee for covered services"
      ]
    }
  ];

  const complianceSections = [
    {
      title: "Safeguarding Policy",
      content: [
        "Comprehensive child protection procedures and protocols",
        "Background checks for all staff and volunteers",
        "Training on child safeguarding and reporting procedures",
        "Clear reporting mechanisms for concerns or incidents"
      ]
    },
    {
      title: "Complaints & Redress",
      content: [
        "Multiple channels for submitting complaints and feedback",
        "Response within 48 hours for urgent matters",
        "Independent review process for unresolved complaints",
        "Regular monitoring and improvement of complaint handling"
      ]
    },
    {
      title: "Quality Assurance",
      content: [
        "Regular audits and compliance reviews",
        "Third-party verification of impact metrics",
        "Continuous improvement of services and processes",
        "Transparent reporting on performance and outcomes"
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary-600 to-accent-600 text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Privacy, Terms & Compliance</h1>
            <p className="text-xl md:text-2xl text-primary-100">
              Your privacy and rights are important to us
            </p>
          </motion.div>
        </div>
      </section>

      {/* Privacy Policy */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Privacy Policy</h2>
            <p className="section-subtitle">
              How we protect and use your personal information
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {privacySections.map((section, index) => {
              const Icon = section.icon;
              return (
                <motion.div
                  key={section.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="card p-8"
                >
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mr-4">
                      <Icon size={24} className="text-primary-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">{section.title}</h3>
                  </div>

                  <ul className="space-y-3">
                    {section.content.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start space-x-3">
                        <CheckCircle size={16} className="text-accent-600 mt-1 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Terms of Service */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Terms of Service</h2>
            <p className="section-subtitle">
              Insurance terms and service conditions
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {termsSections.map((section, index) => {
              const Icon = section.icon;
              return (
                <motion.div
                  key={section.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="card p-8"
                >
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 bg-accent-100 rounded-lg flex items-center justify-center mr-4">
                      <Icon size={24} className="text-accent-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">{section.title}</h3>
                  </div>

                  <ul className="space-y-3">
                    {section.content.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start space-x-3">
                        <CheckCircle size={16} className="text-accent-600 mt-1 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Compliance */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Compliance & Safeguarding</h2>
            <p className="section-subtitle">
              Our commitment to quality, safety, and accountability
            </p>
          </motion.div>

          <div className="space-y-8">
            {complianceSections.map((section, index) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card p-8"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{section.title}</h3>
                <ul className="space-y-4">
                  {section.content.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start space-x-3">
                      <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact for Questions */}
      <section className="section-padding bg-primary-600 text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-4">Questions About Our Policies?</h2>
            <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
              If you have any questions about our privacy policy, terms of service, or compliance procedures, please don't hesitate to contact us.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <Link to="/contact-form" className="btn-secondary">
                Email Privacy Team
              </Link>
              <Link to="/contact-form" className="btn-secondary">
                Email Compliance Team
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyTerms;
